import { useEffect, useRef } from "react";
import Cookies from "js-cookie";
import { encryptValue } from "../utils/cryptoUtils";

const SESSION_DURATION = 20 * 60 * 1000; // 20 minutes
const COOKIE_PREFIX = "sessionTimer";

interface SessionTimerData {
  startedAt: number;
  expiresAt: number;
}

interface UseSessionTimerOptions {
  isLoggedIn: boolean;
  authToken: string | null | undefined;
  onTimeout: () => void | Promise<void>;
}

const isSessionTimerData = (
  value: unknown
): value is SessionTimerData => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const data = value as Record<string, unknown>;

  return (
    typeof data.startedAt === "number" &&
    typeof data.expiresAt === "number"
  );
};

export const useSessionTimer = ({
  isLoggedIn,
  authToken,
  onTimeout,
}: UseSessionTimerOptions): void => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const onTimeoutRef = useRef(onTimeout);

  // Keep the latest callback without restarting the timer.
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    if (!isLoggedIn || !authToken) {
      return;
    }

    let cancelled = false;

    const clearTimer = (): void => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const initializeTimer = async (): Promise<void> => {
      // Create a stable identifier from the AuthToken.
      const tokenHash = await encryptValue(authToken);

      if (cancelled) {
        return;
      }

      const cookieName = `${COOKIE_PREFIX}_${tokenHash}`;

      const getTimerData = (): SessionTimerData | null => {
        const cookie = Cookies.get(cookieName);

        if (!cookie) {
          return null;
        }

        try {
          const parsed: unknown = JSON.parse(cookie);

          if (isSessionTimerData(parsed)) {
            return parsed;
          }
        } catch {
          // Invalid cookie — treat it as a new timer.
        }

        return null;
      };

      const saveTimerData = (
        startedAt: number
      ): SessionTimerData => {
        const timerData: SessionTimerData = {
          startedAt,
          expiresAt: startedAt + SESSION_DURATION,
        };

        Cookies.set(
          cookieName,
          JSON.stringify(timerData),
          {
            expires: 1,
          }
        );

        return timerData;
      };

      const handleTimeout = async (): Promise<void> => {
        if (cancelled) {
          return;
        }

        clearTimer();

        try {
          await onTimeoutRef.current();
        } catch (error) {
          console.error(
            "Session timer API failed:",
            error
          );
        }

        if (cancelled) {
          return;
        }

        // Start the next 20-minute cycle.
        const nextTimer = saveTimerData(Date.now());

        const remainingTime =
          nextTimer.expiresAt - Date.now();

        timeoutRef.current = setTimeout(() => {
          void handleTimeout();
        }, remainingTime);
      };

      const scheduleTimer = (): void => {
        if (cancelled) {
          return;
        }

        clearTimer();

        // Try to find an existing timer first.
        let timerData = getTimerData();

        // No existing timer means this is a new login/session.
        if (!timerData) {
          timerData = saveTimerData(Date.now());
        }

        const remainingTime =
          timerData.expiresAt - Date.now();

        console.log("[useSessionTimer]", {
          startedAt: new Date(
            timerData.startedAt
          ).toISOString(),
          expiresAt: new Date(
            timerData.expiresAt
          ).toISOString(),
          remainingMinutes:
            Math.max(remainingTime, 0) / 60000,
        });

        // Timer already expired.
        if (remainingTime <= 0) {
          void handleTimeout();
          return;
        }

        // Continue from the existing expiration time.
        timeoutRef.current = setTimeout(() => {
          void handleTimeout();
        }, remainingTime);
      };

      scheduleTimer();
    };

    void initializeTimer();

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [isLoggedIn, authToken]);
};